/**
 * Deduplicates concurrent calls to an async function.
 *
 * Stores the in-flight promise in holder[key] so that simultaneous callers
 * share the same execution, and clears it once settled so the next call runs
 * the function again.
 *
 * @param {object} holder Object that holds the pending promise (e.g. a class).
 * @param {string} key Property name used to store the pending promise.
 * @param {Function} fn Function producing the promise to run.
 * @return {Promise} The shared in-flight promise.
 */
export function once<T>(
    holder: object,
    key: string,
    fn: () => T | PromiseLike<T>,
): Promise<T> {
    // Los llamantes pasan la propia clase (this en un metodo estatico), asi que
    // el almacen es un objeto cualquiera indexado por nombre de propiedad.
    const store = holder as Record<string, Promise<T> | undefined>;

    const pending = store[key];
    if (pending) {
        return pending;
    }

    const promise = Promise.resolve(fn()).finally(() => {
        store[key] = undefined;
    });
    store[key] = promise;

    return promise;
}

/**
 * Rejects if the promise does not settle within the given time.
 *
 * The original promise is not cancelled by itself: it keeps running, and
 * whatever it does when it settles is up to its own handlers. To stop it, pass
 * onTimeout (for instance, to abort its requests). The timer is cleared as soon
 * as either side settles, so a promise that wins does not leave a rejection
 * behind.
 *
 * @param {Promise} promise Promise to wait for.
 * @param {number} ms Time limit, in milliseconds.
 * @param {string} message Message of the error on timeout.
 * @param {Function} [onTimeout] Called when the time limit is reached.
 * @return {Promise} Settles like the promise, or rejects on timeout.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, message: string, onTimeout?: () => void): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
            onTimeout?.();
            reject(new Error(message));
        }, ms);
    });

    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
